import { WS_BASE } from '@shared/config/constants'
import { apiClient } from './client'
import { endpoints } from './endpoints'

interface TicketResponse {
  ticket: string
  expires_at: string
}

/** Коды закрытия, которые бэк использует осмысленно. Всё остальное — обычный
 *  обрыв связи. */
export const WsCloseCode = {
  /** Нормальное закрытие нами же. */
  normal: 1000,
  /** Тикет невалиден: просрочен, уже использован, вытеснен новым, либо истёк
   *  access-токен, которым он выпущен. Лечится новым тикетом. */
  unauthorized: 4401,
  /** Роль или владение: участник постучался в группу представителей, чужой
   *  тикет поддержки. Переподключение не поможет — оно только зациклится. */
  forbidden: 4403,
} as const

/** Сколько ждать выпуска тикета и, отдельно, открытия сокета. Без предела
 *  зависший POST или хендшейк (ни `open`, ни `close`) держал общую очередь, и все
 *  остальные сокеты не подключались вовсе. */
export const HANDSHAKE_TIMEOUT_MS = 15_000

/**
 * Выпуск тикета и подключение сокета — строго по одному за раз.
 *
 * Бэк держит **один** pending-тикет на пользователя: выпуск нового мгновенно
 * обнуляет предыдущий (проверено на стенде 2026-08-16 — первый тикет отдаёт
 * 403, второй 101). Страница чата поднимает объявления, приват и уведомления
 * одновременно, поэтому без очереди они затирали бы тикеты друг другу: все,
 * кроме последнего, получали бы 4401, уходили в реконнект и затирали снова.
 *
 * Очередь освобождается только после `open` или `close` — тикет считается
 * потраченным на хендшейке, а не в момент выпуска.
 */
let handshakeQueue: Promise<unknown> = Promise.resolve()

async function issueTicket(): Promise<string> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), HANDSHAKE_TIMEOUT_MS)
  try {
    const { data } = await apiClient.post<TicketResponse>(
      endpoints.auth.websocketTicket,
      {},
      { signal: controller.signal }
    )
    return data.ticket
  } catch (error) {
    // Код 1006 — как у зависшего хендшейка: вызывающий уйдёт в обычный бэкофф.
    if (controller.signal.aborted) throw new WsHandshakeError(1006)
    throw error
  } finally {
    clearTimeout(timer)
  }
}

export interface TicketedSocketOptions {
  /** Путь после `WS_BASE`, например `/chat/announcements/` или `/notifications/`. */
  path: string
  /** Язык интерфейса для бэка (`ru` | `kk` | `en`); опускается, если null. */
  lang?: string | null
}

/**
 * Открывает сокет по одноразовому тикету. Возвращает уже открытый `WebSocket`
 * либо бросает, если хендшейк не удался.
 */
export function connectWithTicket({ path, lang }: TicketedSocketOptions): Promise<WebSocket> {
  const run = handshakeQueue.then(async () => {
    const ticket = await issueTicket()
    const url = new URL(`${WS_BASE}${path}`)
    url.searchParams.set('ticket', ticket)
    if (lang) url.searchParams.set('lang', lang)

    const ws = new WebSocket(url.toString())
    await new Promise<void>((resolve, reject) => {
      const onOpen = () => {
        cleanup()
        resolve()
      }
      const onClose = (event: CloseEvent) => {
        cleanup()
        reject(new WsHandshakeError(event.code))
      }
      // Код 1006 — «обрыв» без причины: вызывающий уйдёт в обычный бэкофф.
      const timer = setTimeout(() => {
        cleanup()
        ws.close()
        reject(new WsHandshakeError(1006))
      }, HANDSHAKE_TIMEOUT_MS)
      const cleanup = () => {
        clearTimeout(timer)
        ws.removeEventListener('open', onOpen)
        ws.removeEventListener('close', onClose)
      }
      ws.addEventListener('open', onOpen)
      ws.addEventListener('close', onClose)
    })
    return ws
  })

  // Очередь не должна вставать колом из-за одного упавшего хендшейка, но и
  // проглатывать ошибку у вызывающего нельзя — поэтому «хвост» очереди гасим
  // отдельно от возвращаемого промиса.
  handshakeQueue = run.catch(() => undefined)
  return run
}

/** Хендшейк закрылся, не открывшись: в `code` — причина от бэка. */
export class WsHandshakeError extends Error {
  readonly code: number

  constructor(code: number) {
    super(`WebSocket handshake failed with code ${code}`)
    this.name = 'WsHandshakeError'
    this.code = code
  }
}
